/**
 * Hunting: when the diamonds in reach will not make the quota, drop boulders where they pay - on
 * a butterfly for nine diamonds, on a firefly beside a vault wall to blast it open, or through a
 * magic wall to come out as diamonds. The hunter marks the landing places (the space each creature
 * roams, magic walls that still work) and finds the ways to send a boulder onto them - push one
 * sideways into the space above, or snap away the dirt under one sitting above - and names the
 * cell Digby must stand on. Whether a drop pays, and timing it, is the look-ahead's job
 * (`Mover.fuse`).
 */

import { E } from '../sim';
import type { CaveState, Input } from '../sim';
import { DIR_LEFT, DIR_RIGHT, moveIn, snapIn } from './inputs';
import { INF as UNREACHED } from './paths';
import type { Paths } from './paths';

const { SPACE, DIRT, STEEL, BOULDER, DIAMOND, FIREFLY, BUTTERFLY, SLIME, MAGIC_WALL } = E;
const INF = UNREACHED;
/** The most space cells followed from one creature. */
const ROAM = 120;
/** The fuse being worked on is kept unless another is this much nearer (no dithering). */
const KEEP_MARGIN = 8;

/** Where to stand and what to press to send a boulder onto a landing place. */
export interface Fuse {
  at: number;
  input: Input;
  /** A push works one scan in four; a snap always does. */
  push: boolean;
}

export class Hunter {
  /** Landing places carry the current mark. */
  private readonly land: Int32Array;
  private readonly queue: Int32Array;
  private mark = 0;

  constructor(n: number) {
    this.land = new Int32Array(n);
    this.queue = new Int32Array(n);
  }

  /** Mark every landing place; false if there are none. */
  private survey(s: CaveState): boolean {
    const { w, h, cells } = s;
    const { land, queue } = this;
    const mark = ++this.mark;
    const magic = s.magic.phase !== 'expired';
    let any = false;
    for (let b = 0; b < cells.length; b++) {
      const el = cells[b] ?? STEEL;
      if (el === MAGIC_WALL && magic && cells[b + w] === SPACE) {
        land[b] = mark;
        any = true;
      }
      if (el < FIREFLY || el >= BUTTERFLY + 4 || land[b] === mark) continue;
      any = true;
      let head = 0;
      let tail = 0;
      queue[tail++] = b;
      land[b] = mark;
      while (head < tail && tail < ROAM) {
        const c = queue[head++] ?? 0;
        const x = c % w;
        for (let k = 0; k < 4; k++) {
          if ((k === 1 && x === w - 1) || (k === 3 && x === 0)) continue;
          const nb = k === 0 ? c - w : k === 1 ? c + 1 : k === 2 ? c + w : c - 1;
          if (nb < 0 || nb >= w * h || land[nb] === mark || cells[nb] !== SPACE) continue;
          land[nb] = mark;
          queue[tail++] = nb;
        }
      }
    }
    return any;
  }

  /** The nearest usable fuse, or null; one standing on `keep` is favoured. */
  plan(s: CaveState, me: number, near: Paths, banned: Int32Array, keep: number): Fuse | null {
    if (!this.survey(s)) return null;
    const { w, cells } = s;
    const { land, mark } = this;
    let best: Fuse | null = null;
    let bestDist = INF;
    const offer = (at: number, input: Input, push: boolean): void => {
      if (at < 0 || at >= cells.length || land[at] === mark || (banned[at] ?? 0) > s.scan) return;
      const raw = at === me ? 0 : (near.dist[at] ?? INF);
      const d = at === keep && raw < INF ? raw - KEEP_MARGIN : raw;
      if (d >= bestDist) return;
      bestDist = d;
      best = { at, input, push };
    };
    // A boulder pushed into `p` from two cells away, if it stands on something.
    const pushInto = (p: number): void => {
      const x = p % w;
      if (x >= 2 && cells[p - 1] === BOULDER && cells[p - 1 + w] !== SPACE) offer(p - 2, moveIn(DIR_RIGHT), true);
      if (x < w - 2 && cells[p + 1] === BOULDER && cells[p + 1 + w] !== SPACE) offer(p + 2, moveIn(DIR_LEFT), true);
    };
    for (let l = w; l < cells.length - w; l++) {
      if (land[l] !== mark) continue;
      if (cells[l] === SPACE) pushInto(l);
      // Up the column: open space (push into it) and slime (loads drip through) lead down here.
      let u = l - w;
      while (u >= w && land[u] !== mark && (cells[u] === SPACE || cells[u] === SLIME)) {
        if (cells[u] === SPACE) pushInto(u);
        u -= w;
      }
      // Then snap the dirt holding up a boulder (or, onto a creature, a diamond), from beside that
      // dirt. A diamond through a magic wall would come out a boulder.
      const load = cells[u - w] ?? STEEL;
      const heavy = load === BOULDER || (load === DIAMOND && cells[l] !== MAGIC_WALL);
      if (u >= w && land[u] !== mark && cells[u] === DIRT && heavy) {
        const x = u % w;
        if (x >= 1) offer(u - 1, snapIn(DIR_RIGHT), false);
        if (x < w - 1) offer(u + 1, snapIn(DIR_LEFT), false);
      }
    }
    return best;
  }
}
